// WOS-BUNDLE's reads in Work mode: the caller's running timer (the top bar's chip), a task's time
// (the drawer's Time section), a board's time total (the board header) and My Tasks sections.
// Every key sits under `["company", co, "work"]`, so a Work write's invalidation refreshes them;
// change pings name them too (live/change-pings.ts). A route an API does not serve yet reads as
// nothing (no timer, no time, no sections), never as an error.
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { pendingAsNull } from "./work-queries";

export const bundleKeys = {
  timer: (co: string) => ["company", co, "work", "timer"] as const,
  /** Under the task's key: the drawer's refresh of the task refreshes its time. */
  taskTime: (co: string, id: string) => ["company", co, "work", "task", id, "time"] as const,
  boardTime: (co: string, id: string) => ["company", co, "work", "board", id, "time"] as const,
  sections: (co: string) => ["company", co, "work", "my-sections"] as const,
};

export type TimerView = NonNullable<Awaited<ReturnType<Api["client"]["timer"]["get"]>>["timer"]>;
export type TaskTime = Awaited<ReturnType<Api["client"]["tasks"]["time"]>>;
export type MySections = Awaited<ReturnType<Api["client"]["mySections"]["list"]>>;

export const timerQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: bundleKeys.timer(co),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.timer.get({ params: { co } })))?.timer ?? null,
  });

export const taskTimeQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: bundleKeys.taskTime(co, id),
    queryFn: () => pendingAsNull(() => api.client.tasks.time({ params: { co, id } })),
  });

export const boardTimeQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: bundleKeys.boardTime(co, id),
    queryFn: () => pendingAsNull(() => api.client.boards.time({ params: { co, id } })),
  });

export const mySectionsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: bundleKeys.sections(co),
    queryFn: () => pendingAsNull(() => api.client.mySections.list({ params: { co } })),
  });

/** A running clock: "4:05" under an hour, "1:04:05" after. */
export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${rest}` : `${m}:${rest}`;
}

/** Booked time: "1 h 15 min", "45 min", "2 h" (whole minutes, at least "1 min" when any). */
export function formatDuration(seconds: number): string {
  const total = seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : 0;
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Seconds since `startedAt` at `now`. */
export const elapsedSince = (startedAt: string, now: number) =>
  Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1000));
